/**
 * Éditeur de la liste d'effets d'une carte.
 *
 * DECISIONS §6.5 définit une **liste fermée de six formes d'effet** : l'éditeur les expose une à
 * une plutôt que de laisser saisir du JSON libre. Changer le type reconstruit l'effet avec des
 * valeurs par défaut valides, ce qui évite de fabriquer un objet hors schéma au passage.
 *
 * La validation n'est pas faite ici : elle a lieu à l'enregistrement, avec le schéma du contrat
 * (`adminCardPatchSchema`), pour que le message d'erreur soit le même que celui du serveur.
 */
import { Bouton } from './Bouton';
import { Champ } from './Champ';
import { Selection } from './Selection';
import { M } from '../catalogue/messages';

import type { OptionSelection } from './Selection';
import type {
  Archetype,
  CardEffect,
  ChampEffet,
  CibleEconomie,
  CibleRessource,
  EffetBonusEffet,
  EffetBonusStat,
  Element,
  StatTour,
} from '@elemora/shared/content';

const OPTIONS_TYPE: readonly OptionSelection[] = [
  { valeur: 'bonusStat', libelle: M.effets.type.bonusStat },
  { valeur: 'bonusEffet', libelle: M.effets.type.bonusEffet },
  { valeur: 'bonusEconomie', libelle: M.effets.type.bonusEconomie },
  { valeur: 'ressource', libelle: M.effets.type.ressource },
  { valeur: 'pvParcours', libelle: M.effets.type.pvParcours },
  { valeur: 'synergie', libelle: M.effets.type.synergie },
];

const OPTIONS_STAT: readonly OptionSelection[] = [
  { valeur: 'degats', libelle: M.effets.statTour.degats },
  { valeur: 'portee', libelle: M.effets.statTour.portee },
  { valeur: 'cadenceMs', libelle: M.effets.statTour.cadenceMs },
  { valeur: 'coutTour', libelle: M.effets.statTour.coutTour },
  { valeur: 'coutAmelioration', libelle: M.effets.statTour.coutAmelioration },
  { valeur: 'vitesseProjectile', libelle: M.effets.statTour.vitesseProjectile },
];

const OPTIONS_CHAMP: readonly OptionSelection[] = [
  { valeur: 'degats', libelle: M.effets.champEffet.degats },
  { valeur: 'duree', libelle: M.effets.champEffet.duree },
  { valeur: 'facteur', libelle: M.effets.champEffet.facteur },
  { valeur: 'cibles', libelle: M.effets.champEffet.cibles },
];

const OPTIONS_CIBLE_ECONOMIE: readonly OptionSelection[] = [
  { valeur: 'eclatsGagnes', libelle: M.effets.economie.eclatsGagnes },
  { valeur: 'coutTour', libelle: M.effets.economie.coutTour },
  { valeur: 'coutAmelioration', libelle: M.effets.economie.coutAmelioration },
];

const OPTIONS_CIBLE_RESSOURCE: readonly OptionSelection[] = [
  { valeur: 'jokers', libelle: M.effets.ressource.jokers },
  { valeur: 'eclatsRun', libelle: M.effets.ressource.eclatsRun },
];

const OPTIONS_ELEMENT: readonly OptionSelection[] = [
  { valeur: 'feu', libelle: M.elements.feu },
  { valeur: 'eau', libelle: M.elements.eau },
  { valeur: 'terre', libelle: M.elements.terre },
  { valeur: 'air', libelle: M.elements.air },
];

const OPTIONS_ELEMENT_OU_AUCUN: readonly OptionSelection[] = [
  { valeur: '', libelle: M.effets.elementAucun },
  ...OPTIONS_ELEMENT,
];

const OPTIONS_ARCHETYPE: readonly OptionSelection[] = [
  { valeur: '', libelle: M.effets.archetypeAucun },
  { valeur: 'mono', libelle: M.effets.archetypeNoms.mono },
  { valeur: 'zone', libelle: M.effets.archetypeNoms.zone },
  { valeur: 'ligne', libelle: M.effets.archetypeNoms.ligne },
  { valeur: 'chain', libelle: M.effets.archetypeNoms.chain },
  { valeur: 'aura', libelle: M.effets.archetypeNoms.aura },
];

/** Effet de synergie autorisé : seulement les deux formes « bonus » (§6.5). */
const OPTIONS_TYPE_SYNERGIE: readonly OptionSelection[] = [
  { valeur: 'bonusStat', libelle: M.effets.type.bonusStat },
  { valeur: 'bonusEffet', libelle: M.effets.type.bonusEffet },
];

/** Construit un effet valide du type demandé — jamais un objet partiel. */
export function effetParDefaut(type: CardEffect['type']): CardEffect {
  switch (type) {
    case 'bonusStat':
      return { type: 'bonusStat', stat: 'degats', valeur: 10 };
    case 'bonusEffet':
      return { type: 'bonusEffet', element: 'feu', champ: 'degats', valeur: 10 };
    case 'bonusEconomie':
      return { type: 'bonusEconomie', cible: 'eclatsGagnes', valeur: 10 };
    case 'ressource':
      return { type: 'ressource', cible: 'jokers', valeur: 1 };
    case 'pvParcours':
      return { type: 'pvParcours', valeur: 5 };
    case 'synergie':
      return {
        type: 'synergie',
        condition: { elements: ['feu'] },
        effet: { type: 'bonusStat', stat: 'degats', valeur: 10 },
      };
  }
}

/** Convertit une saisie en nombre exploitable (un champ vidé vaut 0, jamais `NaN`). */
function enNombre(valeur: string): number {
  const nombre = Number(valeur);
  return Number.isFinite(nombre) ? nombre : 0;
}

/** `element` et `archetype` sont facultatifs et **stricts** : on omet la clé au lieu de la vider. */
function avecElement(effet: EffetBonusStat, valeur: string): EffetBonusStat {
  const { element: _element, ...reste } = effet;
  return valeur === '' ? reste : { ...reste, element: valeur as Element };
}

function avecArchetype(effet: EffetBonusStat, valeur: string): EffetBonusStat {
  const { archetype: _archetype, ...reste } = effet;
  return valeur === '' ? reste : { ...reste, archetype: valeur as Archetype };
}

export interface ProprietesEditeurEffets {
  readonly effets: readonly CardEffect[];
  readonly surChangement: (effets: CardEffect[]) => void;
}

/** Ligne d'effet « bonus de statistique » (forme 1). */
function LigneBonusStat({
  identifiant,
  effet,
  surChangement,
}: {
  readonly identifiant: string;
  readonly effet: EffetBonusStat;
  readonly surChangement: (effet: EffetBonusStat) => void;
}) {
  return (
    <div className="effet__grille">
      <Selection
        id={`${identifiant}-stat`}
        etiquette={M.effets.stat}
        options={OPTIONS_STAT}
        value={effet.stat}
        onChange={(evenement) =>
          surChangement({ ...effet, stat: evenement.target.value as StatTour })
        }
      />
      <Champ
        id={`${identifiant}-valeur`}
        etiquette={M.effets.valeur}
        type="number"
        step="1"
        value={String(effet.valeur)}
        onChange={(evenement) =>
          surChangement({ ...effet, valeur: enNombre(evenement.target.value) })
        }
      />
      <Selection
        id={`${identifiant}-element`}
        etiquette={M.effets.element}
        options={OPTIONS_ELEMENT_OU_AUCUN}
        value={effet.element ?? ''}
        onChange={(evenement) => surChangement(avecElement(effet, evenement.target.value))}
      />
      <Selection
        id={`${identifiant}-archetype`}
        etiquette={M.effets.archetype}
        options={OPTIONS_ARCHETYPE}
        value={effet.archetype ?? ''}
        onChange={(evenement) => surChangement(avecArchetype(effet, evenement.target.value))}
      />
    </div>
  );
}

/** Ligne d'effet « bonus d'effet élémentaire » (forme 2). */
function LigneBonusEffet({
  identifiant,
  effet,
  surChangement,
}: {
  readonly identifiant: string;
  readonly effet: EffetBonusEffet;
  readonly surChangement: (effet: EffetBonusEffet) => void;
}) {
  return (
    <div className="effet__grille">
      <Selection
        id={`${identifiant}-element`}
        etiquette={M.effets.element}
        options={OPTIONS_ELEMENT}
        value={effet.element}
        onChange={(evenement) =>
          surChangement({ ...effet, element: evenement.target.value as Element })
        }
      />
      <Selection
        id={`${identifiant}-champ`}
        etiquette={M.effets.champ}
        options={OPTIONS_CHAMP}
        value={effet.champ}
        onChange={(evenement) =>
          surChangement({ ...effet, champ: evenement.target.value as ChampEffet })
        }
      />
      <Champ
        id={`${identifiant}-valeur`}
        etiquette={M.effets.valeur}
        type="number"
        step="1"
        value={String(effet.valeur)}
        onChange={(evenement) =>
          surChangement({ ...effet, valeur: enNombre(evenement.target.value) })
        }
      />
    </div>
  );
}

/** Champs d'un effet, selon sa forme. */
function ChampsEffet({
  identifiant,
  effet,
  surChangement,
}: {
  readonly identifiant: string;
  readonly effet: CardEffect;
  readonly surChangement: (effet: CardEffect) => void;
}) {
  switch (effet.type) {
    case 'bonusStat':
      return (
        <LigneBonusStat identifiant={identifiant} effet={effet} surChangement={surChangement} />
      );
    case 'bonusEffet':
      return (
        <LigneBonusEffet identifiant={identifiant} effet={effet} surChangement={surChangement} />
      );
    case 'bonusEconomie':
      return (
        <div className="effet__grille">
          <Selection
            id={`${identifiant}-cible`}
            etiquette={M.effets.cible}
            options={OPTIONS_CIBLE_ECONOMIE}
            value={effet.cible}
            onChange={(evenement) =>
              surChangement({ ...effet, cible: evenement.target.value as CibleEconomie })
            }
          />
          <Champ
            id={`${identifiant}-valeur`}
            etiquette={M.effets.valeur}
            type="number"
            step="1"
            value={String(effet.valeur)}
            onChange={(evenement) =>
              surChangement({ ...effet, valeur: enNombre(evenement.target.value) })
            }
          />
        </div>
      );
    case 'ressource':
      return (
        <div className="effet__grille">
          <Selection
            id={`${identifiant}-cible`}
            etiquette={M.effets.cible}
            options={OPTIONS_CIBLE_RESSOURCE}
            value={effet.cible}
            onChange={(evenement) =>
              surChangement({ ...effet, cible: evenement.target.value as CibleRessource })
            }
          />
          <Champ
            id={`${identifiant}-valeur`}
            etiquette={M.effets.valeur}
            type="number"
            step="1"
            value={String(effet.valeur)}
            onChange={(evenement) =>
              surChangement({ ...effet, valeur: enNombre(evenement.target.value) })
            }
          />
        </div>
      );
    case 'pvParcours':
      return (
        <div className="effet__grille">
          <Champ
            id={`${identifiant}-valeur`}
            etiquette={M.effets.valeur}
            type="number"
            step="1"
            value={String(effet.valeur)}
            onChange={(evenement) =>
              surChangement({ ...effet, valeur: Math.trunc(enNombre(evenement.target.value)) })
            }
          />
        </div>
      );
    case 'synergie': {
      const elements = effet.condition.elements;
      const effetInterne = effet.effet;
      return (
        <div className="effet__synergie">
          <fieldset className="effet__fieldset">
            <legend>{M.effets.conditionElements}</legend>
            {OPTIONS_ELEMENT.map((option) => {
              const valeur = option.valeur as Element;
              const coche = elements.includes(valeur);
              return (
                <label className="case" key={option.valeur}>
                  <input
                    type="checkbox"
                    checked={coche}
                    onChange={() => {
                      const suivants = coche
                        ? elements.filter((candidat) => candidat !== valeur)
                        : [...elements, valeur];
                      // Le schéma exige au moins un élément : on refuse de vider la condition.
                      if (suivants.length === 0) return;
                      surChangement({ ...effet, condition: { elements: suivants } });
                    }}
                  />
                  {option.libelle}
                </label>
              );
            })}
          </fieldset>
          <Champ
            id={`${identifiant}-min-tours`}
            etiquette={M.effets.conditionMinTours}
            type="number"
            min="1"
            step="1"
            value={effet.condition.minTours === undefined ? '' : String(effet.condition.minTours)}
            onChange={(evenement) => {
              const valeur = evenement.target.value;
              const suivants = { elements: effet.condition.elements };
              surChangement({
                ...effet,
                condition:
                  valeur === ''
                    ? suivants
                    : { ...suivants, minTours: Math.trunc(enNombre(valeur)) },
              });
            }}
          />
          <Selection
            id={`${identifiant}-type-interne`}
            etiquette={M.effets.conditionEffet}
            options={OPTIONS_TYPE_SYNERGIE}
            value={effetInterne.type}
            onChange={(evenement) => {
              const type = evenement.target.value;
              surChangement({
                ...effet,
                effet:
                  type === 'bonusEffet'
                    ? { type: 'bonusEffet', element: 'feu', champ: 'degats', valeur: 10 }
                    : { type: 'bonusStat', stat: 'degats', valeur: 10 },
              });
            }}
          />
          {effetInterne.type === 'bonusStat' ? (
            <LigneBonusStat
              identifiant={`${identifiant}-interne`}
              effet={effetInterne}
              surChangement={(suivant) => surChangement({ ...effet, effet: suivant })}
            />
          ) : (
            <LigneBonusEffet
              identifiant={`${identifiant}-interne`}
              effet={effetInterne}
              surChangement={(suivant) => surChangement({ ...effet, effet: suivant })}
            />
          )}
        </div>
      );
    }
  }
}

export function EditeurEffets({ effets, surChangement }: ProprietesEditeurEffets) {
  const remplacer = (index: number, effet: CardEffect): void => {
    surChangement(effets.map((courant, rang) => (rang === index ? effet : courant)));
  };
  const supprimer = (index: number): void => {
    surChangement(effets.filter((_courant, rang) => rang !== index));
  };
  const ajouter = (): void => {
    // Le schéma plafonne à deux effets : le bouton est désactivé au-delà.
    surChangement([...effets, effetParDefaut('bonusStat')]);
  };

  return (
    <div className="editeur-effets">
      <p className="panneau__aide">{M.cartes.effetsAide}</p>
      {effets.map((effet, index) => (
        <fieldset className="effet" key={`effet-${String(index)}`}>
          <legend className="effet__titre">{M.cartes.effetsTitre}</legend>
          <div className="effet__entete">
            <Selection
              id={`effet-${String(index)}-type`}
              etiquette={M.cartes.typeEffet}
              options={OPTIONS_TYPE}
              value={effet.type}
              onChange={(evenement) =>
                remplacer(index, effetParDefaut(evenement.target.value as CardEffect['type']))
              }
            />
            <Bouton
              variante="discret"
              onClick={() => supprimer(index)}
              disabled={effets.length <= 1}
            >
              {M.cartes.supprimerEffet}
            </Bouton>
          </div>
          <ChampsEffet
            identifiant={`effet-${String(index)}`}
            effet={effet}
            surChangement={(suivant) => remplacer(index, suivant)}
          />
        </fieldset>
      ))}
      <Bouton variante="secondaire" onClick={ajouter} disabled={effets.length >= 2}>
        {M.cartes.ajouterEffet}
      </Bouton>
    </div>
  );
}
